import { ReactNode, useCallback, useEffect, useState } from 'react'
import Modal from '../../reusable/modal'
import api from '../../../helpers/apiRequester'
import { ButtonSecondary } from '../../buttons/ButtonSecondary'
import { EmptyState } from '../../reusable/emptyState/EmptyState'
import { FileTags, RecordTags } from './FileTags'
import { BatchRowRecord, RowStatusBadge, formatDateTime } from './shared'
import styles from './runsTable.module.scss'

const BATCH_ROW_URL = 'viewthru-batch-row'

/** The list row plus the AffiliaTech transcript kept against it. */
interface BatchRowDetailRecord extends BatchRowRecord {
    updated_at?: string | null
    spreadsheet_json?: unknown
    verify_request_json?: unknown
    verify_response_json?: unknown
    credit_search_request_json?: unknown
    credit_search_response_json?: unknown
    credit_report_request_json?: unknown
    credit_report_response_json?: unknown
    aml_report_request_json?: unknown
    aml_report_response_json?: unknown
    client?: Record<string, unknown> | null
    aml?: Record<string, unknown> | null
    amlCcjs?: Record<string, unknown>[]
    amlInsolvencies?: Record<string, unknown>[]
    agreements?: Record<string, unknown>[]
    previousIndustryMatches?: Record<string, unknown>[]
}

type DetailTab = 'lead' | 'overview' | 'payload' | 'reports' | 'data' | 'json'

type ReportKind = 'credit' | 'aml'

const REPORT_TITLES: Record<ReportKind, string> = { credit: 'Credit report', aml: 'AML report' }

const DETAIL_TABS: { id: DetailTab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'payload', label: 'Payload' },
    { id: 'reports', label: 'PDF reports' },
    { id: 'data', label: 'Results' },
    { id: 'json', label: 'Raw JSON' },
]

const text = (value: unknown) => (value == null || value === '' ? '—' : String(value))

const money = (value: unknown) => {
    const amount = Number(value)
    return value != null && value !== '' && Number.isFinite(amount)
        ? amount.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })
        : '—'
}

/** What each outcome means, in terms of what the run did or did not pay for. */
const STATUS_EXPLANATIONS: Record<string, string> = {
    pending: 'This person had not been sent to AffiliaTech when the batch stopped. Resume picks them up.',
    invalid: 'The spreadsheet row failed validation, so no identity check or credit search was run.',
    duplicate:
        'This identity had already been checked, so the run reused the existing person and their files instead of paying for another search.',
    blocked: 'AffiliaTech would not verify this identity, so no credit search was run and nothing was billed.',
    challenge:
        'AffiliaTech challenged this identity and asked for more verification, so no credit search was run.',
    pdf_pending:
        'The credit search was billed and the data was saved, but the PDF report did not come back. Resume retries the PDF only — it does not search again.',
    error: 'The run stopped on this person. The message below is what the failing step reported.',
    success: 'Everything completed: identity checked, credit search billed and saved, and the files stored.',
}

const JSON_SECTIONS: { key: keyof BatchRowDetailRecord; label: string }[] = [
    { key: 'spreadsheet_json', label: 'Spreadsheet row as parsed' },
    { key: 'verify_request_json', label: 'Identity check — request' },
    { key: 'verify_response_json', label: 'Identity check — response' },
    { key: 'credit_search_request_json', label: 'Credit search — request' },
    { key: 'credit_search_response_json', label: 'Credit search — response' },
    { key: 'credit_report_request_json', label: 'Credit report PDF — request' },
    { key: 'credit_report_response_json', label: 'Credit report PDF — outcome' },
    { key: 'aml_report_request_json', label: 'AML report PDF — request' },
    { key: 'aml_report_response_json', label: 'AML report PDF — outcome' },
]

/**
 * JSON columns arrive either already parsed or as text depending on the driver,
 * so accept both and fall back to the raw string when it will not parse.
 */
const prettyJson = (value: unknown): string | null => {
    if (value == null) return null
    let data = value
    if (typeof value === 'string') {
        const text = value.trim()
        if (!text) return null
        try {
            data = JSON.parse(text)
        } catch {
            return text
        }
    }
    try {
        return JSON.stringify(data, null, 2)
    } catch {
        return String(data)
    }
}

const JsonSection = ({ label, body }: { label: string; body: string }) => {
    const [copied, setCopied] = useState(false)

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(body)
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
        } catch {
            setCopied(false)
        }
    }

    return (
        <details className={styles.jsonSection}>
            <summary>
                <span>{label}</span>
                <button
                    type="button"
                    onClick={(event) => {
                        event.preventDefault()
                        void copy()
                    }}
                >
                    {copied ? 'Copied' : 'Copy'}
                </button>
            </summary>
            <pre>{body}</pre>
        </details>
    )
}

interface BatchRowDetailProps {
    row: BatchRowRecord
    onClose: () => void
    /** Keeps the batch table's file tags in step when an AML report arrives. */
    onRowUpdated?: (patch: Partial<BatchRowRecord> & { id: number }) => void
    /**
     * An extra first tab supplied by the caller — the eligibility analysis
     * opens the row this way so the verdict sits alongside the transcript.
     */
    leadTab?: { label: string; content: ReactNode }
    /** Replaces the "Spreadsheet row N" line under the name. */
    subtitle?: ReactNode
}

export const BatchRowDetail = ({ row, onClose, onRowUpdated, leadTab, subtitle }: BatchRowDetailProps) => {
    const [detail, setDetail] = useState<BatchRowDetailRecord | null>(null)
    const [loading, setLoading] = useState(true)
    const [failure, setFailure] = useState<string | null>(null)
    const [tab, setTab] = useState<DetailTab>(leadTab ? 'lead' : 'overview')
    const [openingReport, setOpeningReport] = useState<ReportKind | null>(null)
    const [inlineReport, setInlineReport] = useState<{ kind: ReportKind; url: string } | null>(null)
    const [reportFailure, setReportFailure] = useState<string | null>(null)
    const [amlRetrying, setAmlRetrying] = useState(false)
    const [amlOutcome, setAmlOutcome] = useState<{ tone: 'error' | 'success'; text: string } | null>(null)

    const loadDetail = useCallback(async () => {
        setLoading(true)
        try {
            const response = await api({ url: BATCH_ROW_URL, data: { rowId: row.id } })
            if (!response.data?.success) throw new Error(response.data?.message || 'Could not load this row.')
            setDetail(response.data.row)
            setFailure(null)
        } catch (error: any) {
            setDetail(null)
            setFailure(
                error?.response?.data?.message || error?.message || 'The request for this row failed.'
            )
        } finally {
            setLoading(false)
        }
    }, [row.id])

    useEffect(() => {
        void loadDetail()
    }, [loadDetail])

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [onClose])

    /** `inline` embeds the PDF in the modal; `tab` opens it in a new browser tab. */
    const viewReport = async (kind: ReportKind, target: 'inline' | 'tab' = 'inline') => {
        if (openingReport) return
        setOpeningReport(kind)
        setReportFailure(null)
        try {
            const response = await api({
                url: 'viewthru-batch-file',
                data: { rowId: row.id, kind, view: true },
            })
            if (!response.data?.success || !response.data.url) {
                throw new Error(response.data?.message || 'Could not prepare this report.')
            }
            if (target === 'tab') window.open(response.data.url, '_blank', 'noopener,noreferrer')
            else setInlineReport({ kind, url: response.data.url })
        } catch (error: any) {
            setReportFailure(
                error?.response?.data?.message || error?.message || 'Could not open this report.'
            )
        } finally {
            setOpeningReport(null)
        }
    }

    const rerequestAml = async () => {
        if (amlRetrying) return
        setAmlRetrying(true)
        setAmlOutcome(null)
        try {
            const response = await api({ url: 'viewthru-batch-aml-rerequest', data: { rowId: row.id } })
            const key = response.data?.amlReportKey || null
            if (!response.data?.success) {
                // A "no report available" answer is an outcome, not a failure of
                // the request — AffiliaTech often simply has no AML document.
                setAmlOutcome({
                    tone: 'error',
                    text: response.data?.message || 'AffiliaTech returned no AML report.',
                })
                return
            }
            setAmlOutcome({ tone: 'success', text: response.data?.message || 'AML report saved.' })
            if (key) {
                setDetail((existing) => (existing ? { ...existing, aml_report_key: key } : existing))
                onRowUpdated?.({ id: row.id, aml_report_key: key })
            }
        } catch (error: any) {
            setAmlOutcome({
                tone: 'error',
                text:
                    error?.response?.data?.message ||
                    error?.message ||
                    'Could not re-request the AML report.',
            })
        } finally {
            setAmlRetrying(false)
        }
    }

    const current = detail ?? row
    const client = detail?.client ?? null
    const name = `${current.firstname || ''} ${current.lastname || ''}`.trim() || 'Unnamed person'

    /** Prefer the saved person record; fall back to what the list row carries. */
    const fromClient = (key: string, fallback?: unknown) => {
        const value = client?.[key]
        return value == null || value === '' ? fallback ?? null : value
    }

    const fullName =
        [fromClient('title'), fromClient('firstname', current.firstname), fromClient('middle_name'), fromClient('lastname', current.lastname)]
            .filter((part) => part != null && part !== '')
            .join(' ')
            .trim() || name

    const addressLines = [
        [fromClient('flat'), fromClient('house_name')].filter(Boolean).join(' '),
        [fromClient('house_number'), fromClient('street')].filter(Boolean).join(' '),
        fromClient('town_city'),
        fromClient('county'),
        fromClient('postcode', current.postcode),
    ]
        .map((line) => (line == null ? '' : String(line).trim()))
        .filter(Boolean)

    const personFacts: [string, string][] = [
        ['Full name', fullName],
        ['Date of birth', text(fromClient('dob', current.dob))],
        ['Email', text(fromClient('email'))],
        ['Telephone', text(fromClient('telephone'))],
    ]

    const recordFacts: [string, string][] = [
        ['Client ID', current.client_id ? String(current.client_id) : 'Not saved'],
        ['Batch row ID', String(current.id)],
        ['Client reference', text(current.client_reference)],
        ['Provider reference', text(current.provider_reference)],
        [
            'Reused client',
            current.duplicate_of_client_id ? String(current.duplicate_of_client_id) : '—',
        ],
        ['Duplicate source', current.duplicate_source_label || current.duplicate_source || '—'],
        ['Row added', formatDateTime(current.created_at)],
        ['Row updated', formatDateTime(detail?.updated_at ?? null)],
        ['Client created', formatDateTime((fromClient('created_at') as string | null) ?? null)],
        ['Client updated', formatDateTime((fromClient('updated_at') as string | null) ?? null)],
    ]

    const sections = JSON_SECTIONS.map((section) => ({
        label: section.label,
        body: prettyJson(detail?.[section.key]),
    })).filter((section): section is { label: string; body: string } => Boolean(section.body))
    const payloadSections = sections.filter(
        (section) =>
            section.label.includes('parsed') || section.label.includes('request')
    )
    const responseSections = sections.filter((section) => !payloadSections.includes(section))
    const agreements = detail?.agreements ?? []
    const previousIndustryMatches = detail?.previousIndustryMatches ?? []
    const amlCcjs = detail?.amlCcjs ?? []
    const amlInsolvencies = detail?.amlInsolvencies ?? []

    return (
        <Modal modalState previewModalAction={onClose}>
            <div className={styles.rowDetail}>
                <div className={styles.rowDetailHeader}>
                    <div>
                        <h3>{name}</h3>
                        <span>{subtitle ?? `Spreadsheet row ${current.row_number}`}</span>
                    </div>
                    <RowStatusBadge row={current} />
                </div>

                <div className={styles.rowDetailTabs}>
                    {(leadTab ? [{ id: 'lead' as DetailTab, label: leadTab.label }, ...DETAIL_TABS] : DETAIL_TABS).map(
                        (item) => (
                            <button
                                key={item.id}
                                type="button"
                                className={tab === item.id ? styles.rowDetailTabActive : ''}
                                onClick={() => setTab(item.id)}
                            >
                                {item.label}
                                {item.id === 'data' && detail
                                    ? ` (${agreements.length + previousIndustryMatches.length})`
                                    : ''}
                            </button>
                        )
                    )}
                </div>

                {tab === 'lead' && leadTab?.content}

                {tab !== 'lead' && failure ? (
                    <EmptyState
                        bare
                        tone="error"
                        title="Could not load the full transcript"
                        description={failure}
                        action={<ButtonSecondary onClick={loadDetail}>Try again</ButtonSecondary>}
                    />
                ) : tab !== 'lead' && loading ? (
                    <EmptyState bare tone="loading" title="Loading the AffiliaTech transcript" />
                ) : null}

                {!loading && !failure && tab === 'overview' && (
                    <div className={styles.overview}>
                        <p className={styles.rowDetailLead}>
                            {STATUS_EXPLANATIONS[current.status] ||
                                'No explanation is recorded for this outcome.'}
                        </p>

                        {current.error_message && (
                            <p className={styles.detailError}>{current.error_message}</p>
                        )}

                        <section>
                            <h4>Person</h4>
                            <div className={styles.rowDetailFacts}>
                                {personFacts.map(([label, value]) => (
                                    <div key={label}>
                                        <span className={styles.detailLabel}>{label}</span>
                                        <span className={styles.detailValue}>{value}</span>
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section>
                            <h4>Address</h4>
                            {addressLines.length ? (
                                <address className={styles.addressBlock}>
                                    {addressLines.map((line) => (
                                        <span key={line}>{line}</span>
                                    ))}
                                </address>
                            ) : (
                                <p className={styles.rowDetailNoProblem}>
                                    No address was saved for this person.
                                </p>
                            )}
                        </section>

                        <section>
                            <h4>Record</h4>
                            <div className={styles.rowDetailFacts}>
                                {recordFacts.map(([label, value]) => (
                                    <div key={label}>
                                        <span className={styles.detailLabel}>{label}</span>
                                        <span className={styles.detailValue}>{value}</span>
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section>
                            <h4>Saved files</h4>
                            <FileTags row={current} layout="row" />
                        </section>

                        <section>
                            <h4>Saved records</h4>
                            <RecordTags row={current} layout="row" />
                        </section>
                    </div>
                )}

                {!loading && !failure && tab === 'payload' && (
                    <div className={styles.jsonSections}>
                        {payloadSections.length ? (
                            payloadSections.map((section) => (
                                <JsonSection key={section.label} label={section.label} body={section.body} />
                            ))
                        ) : (
                            <p className={styles.rowDetailNoProblem}>No request payload was stored.</p>
                        )}
                    </div>
                )}

                {!loading && !failure && tab === 'reports' && (
                    <div className={styles.reportViewer}>
                        <p className={styles.rowDetailLead}>
                            View a report here in the modal or open it in a new browser tab. The saved-file tags
                            below download it.
                        </p>
                        <div className={styles.reportCards}>
                            <div>
                                <strong>Credit report</strong>
                                <span>{current.credit_report_key ? 'PDF saved' : 'Not available'}</span>
                                <div className={styles.reportCardActions}>
                                    <ButtonSecondary
                                        disabled={!current.credit_report_key || openingReport !== null}
                                        onClick={() => void viewReport('credit')}
                                    >
                                        {openingReport === 'credit' ? 'Opening…' : 'View here'}
                                    </ButtonSecondary>
                                    <ButtonSecondary
                                        disabled={!current.credit_report_key || openingReport !== null}
                                        onClick={() => void viewReport('credit', 'tab')}
                                    >
                                        New tab
                                    </ButtonSecondary>
                                </div>
                            </div>
                            <div>
                                <strong>AML report</strong>
                                <span>{current.aml_report_key ? 'PDF saved' : 'Not available'}</span>
                                <div className={styles.reportCardActions}>
                                    <ButtonSecondary
                                        disabled={!current.aml_report_key || openingReport !== null}
                                        onClick={() => void viewReport('aml')}
                                    >
                                        {openingReport === 'aml' ? 'Opening…' : 'View here'}
                                    </ButtonSecondary>
                                    {current.aml_report_key && (
                                        <ButtonSecondary
                                            disabled={openingReport !== null}
                                            onClick={() => void viewReport('aml', 'tab')}
                                        >
                                            New tab
                                        </ButtonSecondary>
                                    )}
                                    {!current.aml_report_key && (
                                        <ButtonSecondary
                                            disabled={amlRetrying || !current.provider_reference}
                                            onClick={() => void rerequestAml()}
                                        >
                                            {amlRetrying ? 'Re-requesting…' : 'Re-request AML'}
                                        </ButtonSecondary>
                                    )}
                                </div>
                            </div>
                        </div>
                        {!current.aml_report_key && (
                            <p className={styles.rowDetailLead}>
                                {current.provider_reference
                                    ? 'Re-requesting asks AffiliaTech for the AML PDF again using the reference from the credit search that was already billed. Nothing is searched or charged a second time. AffiliaTech often has no AML document for a person, in which case this will keep coming back empty.'
                                    : 'This person has no AffiliaTech reference, so there is no completed credit search to pull an AML report from.'}
                            </p>
                        )}
                        {amlOutcome && (
                            <p
                                className={
                                    amlOutcome.tone === 'error' ? styles.detailError : styles.importSuccess
                                }
                                role={amlOutcome.tone === 'error' ? 'alert' : 'status'}
                            >
                                {amlOutcome.text}
                            </p>
                        )}
                        {reportFailure && <p className={styles.detailError}>{reportFailure}</p>}
                        {inlineReport && (
                            <section className={styles.pdfViewer}>
                                <div className={styles.pdfViewerBar}>
                                    <h4>{REPORT_TITLES[inlineReport.kind]}</h4>
                                    <div>
                                        <button
                                            type="button"
                                            onClick={() => window.open(inlineReport.url, '_blank', 'noopener,noreferrer')}
                                        >
                                            Open in new tab
                                        </button>
                                        <button type="button" onClick={() => setInlineReport(null)}>
                                            Close viewer
                                        </button>
                                    </div>
                                </div>
                                <iframe
                                    key={inlineReport.url}
                                    src={inlineReport.url}
                                    title={`${REPORT_TITLES[inlineReport.kind]} for ${name}`}
                                />
                                <p className={styles.rowDetailNoProblem}>
                                    If the document does not render here, the link expires after five minutes —
                                    click View here again to refresh it.
                                </p>
                            </section>
                        )}
                        <section>
                            <h4>Downloads</h4>
                            <FileTags row={current} layout="row" />
                        </section>
                    </div>
                )}

                {!loading && !failure && tab === 'data' && (
                    <div className={styles.resultSections}>
                        <section>
                            <h4>Agreements ({agreements.length})</h4>
                            {agreements.length ? (
                                <div className={styles.tableWrapper}>
                                    <table className={`${styles.runsTable} ${styles.detailDataTable}`}>
                                        <thead>
                                            <tr>
                                                <th>Policy / account</th>
                                                <th>Lender</th>
                                                <th>Type</th>
                                                <th>Start</th>
                                                <th>Original</th>
                                                <th>Balance</th>
                                                <th>Monthly</th>
                                                <th>Term</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {agreements.map((agreement, index) => (
                                                <tr key={text(agreement.id || index)}>
                                                    <td>{text(agreement.account_id)}</td>
                                                    <td>{text(agreement.lender_name)}</td>
                                                    <td>{text(agreement.account_type)}</td>
                                                    <td>{text(agreement.start_date)}</td>
                                                    <td>{money(agreement.original_amount)}</td>
                                                    <td>{money(agreement.balance)}</td>
                                                    <td>{money(agreement.monthly_payment_amount)}</td>
                                                    <td>{text(agreement.loan_term)}</td>
                                                    <td>{text(agreement.status)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p className={styles.rowDetailNoProblem}>No agreements were returned.</p>
                            )}
                        </section>

                        <section>
                            <h4>AML screening</h4>
                            {detail?.aml ? (
                                <div className={styles.rowDetailFacts}>
                                    {[
                                        ['Identity result', text(detail.aml.identity_result)],
                                        ['Identity score', text(detail.aml.identity_score)],
                                        ['Address verified', text(detail.aml.address_verified)],
                                        ['Electoral roll', text(detail.aml.electoral_roll)],
                                        ['PEPs', text(detail.aml.peps_count)],
                                        ['Sanctions', text(detail.aml.sanctions_count)],
                                        ['RCAs', text(detail.aml.rcas_count)],
                                        ['CIFAS', text(detail.aml.cifas)],
                                        ['CCJs', text(detail.aml.ccj_count)],
                                        ['IVA', Number(detail.aml.has_iva) ? 'Yes' : 'No'],
                                    ].map(([label, value]) => (
                                        <div key={label}>
                                            <span className={styles.detailLabel}>{label}</span>
                                            <span className={styles.detailValue}>{value}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className={styles.rowDetailNoProblem}>No AML record was saved.</p>
                            )}
                            {(amlCcjs.length > 0 || amlInsolvencies.length > 0) && (
                                <p className={styles.rowDetailLead}>
                                    {amlCcjs.length} CCJ record(s) · {amlInsolvencies.length} insolvency/IVA
                                    record(s). Full records are available under Raw JSON.
                                </p>
                            )}
                        </section>

                        <section>
                            <h4>Previous industry searches ({previousIndustryMatches.length})</h4>
                            <p className={styles.rowDetailLead}>
                                These are the previous-industry matches returned in the credit-search JSON and
                                saved for this client.
                            </p>
                            {previousIndustryMatches.length ? (
                                <div className={styles.tableWrapper}>
                                    <table className={`${styles.runsTable} ${styles.detailDataTable}`}>
                                        <thead>
                                            <tr>
                                                <th>Company</th>
                                                <th>Canonical name</th>
                                                <th>Industry</th>
                                                <th>Search date</th>
                                                <th>Search type</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {previousIndustryMatches.map((match, index) => (
                                                <tr key={text(match.id || index)}>
                                                    <td>{text(match.company_name)}</td>
                                                    <td>{text(match.canonical_name)}</td>
                                                    <td>{text(match.industry)}</td>
                                                    <td>{text(match.search_date)}</td>
                                                    <td>{text(match.search_type)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p className={styles.rowDetailNoProblem}>
                                    No previous industry searches were returned for this client.
                                </p>
                            )}
                        </section>
                    </div>
                )}

                {!loading && !failure && tab === 'json' && (
                    <div className={styles.jsonSections}>
                        {[
                            ...responseSections,
                            {
                                label: 'Normalised client record',
                                body: prettyJson(detail?.client),
                            },
                            { label: 'Normalised AML record', body: prettyJson(detail?.aml) },
                            { label: 'AML CCJ records', body: prettyJson(amlCcjs) },
                            { label: 'AML insolvency records', body: prettyJson(amlInsolvencies) },
                            { label: 'Agreement records', body: prettyJson(agreements) },
                            {
                                label: 'Previous industry search records',
                                body: prettyJson(previousIndustryMatches),
                            },
                        ]
                            .filter(
                                (section): section is { label: string; body: string } =>
                                    Boolean(section.body)
                            )
                            .map((section) => (
                                <JsonSection key={section.label} label={section.label} body={section.body} />
                            ))}
                    </div>
                )}
            </div>
        </Modal>
    )
}
